/* ═══════════════════════════════════════════════════════════════
   Forma Studio — the ONE selection (SYNC_PLAN §3.0, DESIGN §3.4).

   Before: four states (zone band, focused level, selected tokens, the
   End-of-Section flag) and three Escape listeners. After: one state object,
   owned by Studio.tsx, holding one ordered key list:

     level:N          a sample body level
     token:<zone>:<i> a header / footer / pre / post token (the existing key)
     eos              the End of Section line
     band:<kind>      the zone band the palette's click-to-add targets

   Explorer semantics: click = replace, Ctrl = toggle, Shift = range within
   a kind (levels, or one zone's tokens), marquee stays for tokens. Mixed
   kinds are allowed. It clears ONLY on a click on empty paper, Escape, or a
   forma switch (the caller). `selKey`/`menuKey` ride in the same object:
   `selKey` is the palette/paste target (it also names a column slot, which
   is not a formattable thing) and `menuKey` the one open context menu.

   The Format Bar edits `keys`; the Levels panel edits `levelFocus`.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { nextSelectionByKey, type SelectionMods } from '../lib/listSelection';
import { nextTokenSelection } from './canvasSelection';
import type { BandKind, StyleSelection } from './formaZonesModel';

/** A click on empty paper clears the selection ONLY when unmodified — a
 *  Ctrl/Cmd/Shift click is an Explorer add/range gesture whose own handler
 *  runs after mouse-up, so clearing here would turn it into a replace. */
export function shouldClearOnEmptyClick(m: {
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
}): boolean {
  return !(m.ctrlKey || m.metaKey || m.shiftKey);
}

export const EOS_KEY = 'eos';
export const levelKey = (n: number): string => `level:${n}`;
export const bandKey = (b: BandKind): string => `band:${b}`;

interface SelState {
  keys: string[];
  selKey: string | null;
  menuKey: string | null;
  /** The level the Levels panel (geometry) and the ruler point at — the last
   *  level clicked. */
  focusLevel: number | null;
}

const EMPTY: SelState = { keys: [], selKey: null, menuKey: null, focusLevel: null };

export interface SelectOpts {
  menu?: boolean;
  additive?: boolean;
  shift?: boolean;
  keepSelection?: boolean;
  x?: number;
  y?: number;
}

export interface FormaSelection {
  /** Every selected key, in click order (the Format Bar's target set). */
  keys: string[];
  selKey: string | null;
  menuKey: string | null;
  tokens: string[];
  levels: number[];
  eos: boolean;
  band: BandKind | null;
  /** The level the Levels panel / ruler / grips point at, or null. */
  levelFocus: number | null;
  /** The band as the older `StyleSelection` shape the palettes still speak. */
  styleSel: StyleSelection;
  /** True when at least one thing the Format Bar can edit is selected. */
  hasFormattable: boolean;
  select: (key: string | null, opts?: SelectOpts) => void;
  selectMany: (keys: string[], additive?: boolean) => void;
  selectLevel: (level: number, mods?: Partial<SelectionMods>) => void;
  selectEos: (mods?: Partial<SelectionMods>) => void;
  setBand: (band: BandKind | null) => void;
  /** Drop token/band/slot selection (page-variant switch, token move) —
   *  levels and EOS stay. */
  clearZone: () => void;
  /** Drop EVERYTHING — white-space click, Escape, forma switch. */
  clear: () => void;
}

export function useFormaSelection(forma: StandardConfig | null): FormaSelection {
  const [st, setSt] = React.useState<SelState>(EMPTY);
  // Mirror so two gestures in one tick (the second reads what the first just
  // made) never see a stale render's state — Explorer's anchor is a gesture
  // fact, not render state.
  const ref = React.useRef<SelState>(EMPTY);
  const anchorRef = React.useRef<string | null>(null);
  const formaRef = React.useRef(forma);
  formaRef.current = forma;

  const commit = React.useCallback((next: SelState) => {
    ref.current = next;
    setSt(next);
  }, []);

  const clear = React.useCallback(() => {
    anchorRef.current = null;
    if (ref.current === EMPTY) return;
    commit(EMPTY);
  }, [commit]);

  const clearZone = React.useCallback(() => {
    anchorRef.current = null;
    const p = ref.current;
    commit({
      ...p,
      keys: p.keys.filter((k) => !k.startsWith('token:') && !k.startsWith('band:')),
      selKey: null,
      menuKey: null,
    });
  }, [commit]);

  const select = React.useCallback(
    (key: string | null, opts?: SelectOpts) => {
      if (!key) {
        clear();
        return;
      }
      const p = ref.current;
      if (key.startsWith('token:')) {
        let keys: string[];
        if (opts?.keepSelection) {
          // Right-click inside a multi-selection: never disturb it.
          keys = p.keys.includes(key) ? p.keys : [...p.keys, key];
          anchorRef.current = key;
        } else if (opts?.additive || opts?.shift) {
          const r = nextTokenSelection(formaRef.current, p.keys, anchorRef.current, key, {
            ctrl: !!opts?.additive,
            shift: !!opts?.shift,
          });
          keys = r.selection;
          anchorRef.current = r.anchor;
        } else {
          keys = [key];
          anchorRef.current = key;
        }
        commit({ ...p, keys, selKey: key, menuKey: opts?.menu ? key : null });
        return;
      }
      // A column slot (or any non-formattable target): it becomes the
      // palette/paste target and drops token selection, nothing else.
      anchorRef.current = null;
      commit({
        ...p,
        keys: p.keys.filter((k) => !k.startsWith('token:')),
        selKey: key,
        menuKey: opts?.menu ? key : null,
      });
    },
    [clear, commit]
  );

  const selectMany = React.useCallback(
    (keys: string[], additive?: boolean) => {
      const p = ref.current;
      const tokens = keys.filter((k) => k.startsWith('token:'));
      const next = additive ? [...new Set([...p.keys, ...tokens])] : tokens;
      const last = tokens.length ? tokens[tokens.length - 1] : null;
      // The last token the box swept up anchors the next Shift range, exactly
      // as the last item a rubber-band touches does in Explorer.
      anchorRef.current = last;
      commit({
        ...p,
        keys: next,
        selKey: last,
        menuKey: null,
        focusLevel: next.includes(levelKey(p.focusLevel ?? -1)) ? p.focusLevel : null,
      });
    },
    [commit]
  );

  const selectLevel = React.useCallback(
    (level: number, mods?: Partial<SelectionMods>) => {
      const p = ref.current;
      const key = levelKey(level);
      const count = Math.max(1, formaRef.current?.levels?.length || 9);
      const list = Array.from({ length: count }, (_v, i) => levelKey(i + 1));
      const r = nextSelectionByKey(p.keys, anchorRef.current, key, list, {
        ctrl: !!mods?.ctrl,
        shift: !!mods?.shift,
      });
      anchorRef.current = r.anchor;
      const still = r.selection.includes(key);
      const lastLevel = [...r.selection].reverse().find((k) => k.startsWith('level:'));
      commit({
        keys: r.selection,
        selKey: mods?.ctrl ? p.selKey : null,
        menuKey: null,
        focusLevel: still ? level : lastLevel ? Number(lastLevel.slice(6)) : null,
      });
    },
    [commit]
  );

  const selectEos = React.useCallback(
    (mods?: Partial<SelectionMods>) => {
      const p = ref.current;
      anchorRef.current = null;
      const has = p.keys.includes(EOS_KEY);
      const keys = mods?.ctrl
        ? has
          ? p.keys.filter((k) => k !== EOS_KEY)
          : [...p.keys, EOS_KEY]
        : [EOS_KEY];
      commit({
        keys,
        selKey: mods?.ctrl ? p.selKey : null,
        menuKey: null,
        focusLevel: mods?.ctrl ? p.focusLevel : null,
      });
    },
    [commit]
  );

  const setBand = React.useCallback(
    (band: BandKind | null) => {
      const p = ref.current;
      const rest = p.keys.filter((k) => !k.startsWith('band:'));
      commit({ ...p, keys: band ? [...rest, bandKey(band)] : rest });
    },
    [commit]
  );

  // The ONE Escape listener. Skips keystrokes that belong to a control (the
  // bar's size field, a popover's own dismissal, a text editor).
  const live = st.keys.length > 0 || st.selKey != null || st.menuKey != null;
  React.useEffect(() => {
    if (!live) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (/^(input|textarea|select)$/i.test(t.tagName) || t.isContentEditable)) return;
      clear();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [live, clear]);

  return React.useMemo<FormaSelection>(() => {
    const tokens = st.keys.filter((k) => k.startsWith('token:'));
    const levels = st.keys.filter((k) => k.startsWith('level:')).map((k) => Number(k.slice(6)));
    const bandK = st.keys.find((k) => k.startsWith('band:'));
    const band = bandK ? (bandK.slice(5) as BandKind) : null;
    const lastLevel = levels.length ? levels[levels.length - 1] : null;
    const levelFocus =
      st.focusLevel != null && levels.includes(st.focusLevel) ? st.focusLevel : lastLevel;
    return {
      keys: st.keys,
      selKey: st.selKey,
      menuKey: st.menuKey,
      tokens,
      levels,
      eos: st.keys.includes(EOS_KEY),
      band,
      levelFocus,
      styleSel: band ? { kind: 'zone', band } : null,
      hasFormattable: tokens.length > 0 || levels.length > 0 || st.keys.includes(EOS_KEY),
      select,
      selectMany,
      selectLevel,
      selectEos,
      setBand,
      clearZone,
      clear,
    };
  }, [st, select, selectMany, selectLevel, selectEos, setBand, clearZone, clear]);
}
